import {MouseEvent, ReactElement, useState} from 'react';
import {Dialog, DialogContent, IconButton, Menu, MenuItem, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useQueryClient} from 'react-query';
import Flag from 'components/shared/ui/Flag';
import LoadingIndicator from 'components/shared/ui/LoadingIndicator';
import {USER} from 'const/backend';
import {Locale, LOCALES} from 'const/locales';
import useUserContext from 'contexts/UserContext/useUserContext';
import useApiMutation from 'hooks/useApiMutation';

export default function LocaleSelector(): ReactElement {
  const queryClient = useQueryClient();
  const {t, i18n} = useTranslation('common');
  const [localeMenuElement, setLocaleMenuElement] = useState<null | HTMLElement>(null);
  const isLocaleMenuOpened = Boolean(localeMenuElement);
  const user = useUserContext();
  const {mutate: patchUser, apiMutationStatus: patchUserRequestStatus} = useApiMutation<{
    name: string;
    locale: string;
  }>();

  function handleOpenLocaleMenu(event: MouseEvent<HTMLButtonElement>) {
    setLocaleMenuElement(event.currentTarget);
  }

  function handleCloseLocaleMenu() {
    setLocaleMenuElement(null);
  }

  function handleSelectLocale(locale: Locale) {
    setLocaleMenuElement(null);
    patchUser(
      {
        path: USER,
        method: 'PATCH',
        body: {
          locale
        }
      },
      {
        onSuccess: function () {
          queryClient.invalidateQueries();
          i18n.changeLanguage(locale);
        }
      }
    );
  }

  const currentLocale = LOCALES.find((locale) => user.locale === locale.id) || LOCALES[0];

  return (
    <>
      <IconButton
        id="locale-menu-button"
        onClick={handleOpenLocaleMenu}
        aria-controls={isLocaleMenuOpened ? 'locale-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isLocaleMenuOpened ? 'true' : undefined}
        sx={{p: 0, width: 28, height: 28}}
      >
        <Flag src={currentLocale.flag} alt={currentLocale.name} />
      </IconButton>
      <Menu
        id="locale-menu"
        anchorEl={localeMenuElement}
        open={isLocaleMenuOpened}
        onClose={handleCloseLocaleMenu}
        MenuListProps={{
          'aria-labelledby': 'locale-menu-button'
        }}
      >
        {LOCALES.map((locale) => (
          <MenuItem key={locale.id} onClick={() => handleSelectLocale(locale.id)}>
            <Flag src={locale.flag} alt={locale.name} sx={{mr: 1}} />
            <Typography>{locale.name}</Typography>
          </MenuItem>
        ))}
      </Menu>
      <Dialog open={patchUserRequestStatus.isLoading}>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <LoadingIndicator text={t('common:changingLocale')} />
        </DialogContent>
      </Dialog>
    </>
  );
}
